












































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { inject } from 'inversify-props';
import { debounce } from 'lodash';
import { InjectionIdEnum } from '@/enums/injection-id.enum';
import { ClientTypeEnum } from '@/enums/client-type.enum';
import ClientNoteService from '@/services/crm/client-note.service';
import ClientNoteModel from '@/models/crm/client-note.model';
import ConfirmationDialog from '@/components/confirmation-dialog.vue';

const pageLimit = 20;

@Component({
  components: { ConfirmationDialog },
})
export default class CrmClientNotesDialog extends Vue {
  @inject(InjectionIdEnum.CrmClientNoteService)
  private clientNoteService!: ClientNoteService;

  @Prop({ required: true })
  readonly clientId!: string;

  @Prop({ required: true })
  readonly clientType!: ClientTypeEnum;

  @Prop({ default: false })
  readonly active!: boolean;

  items: ClientNoteModel[] = [];

  total = 0;

  keyword = '';

  newObservacao = '';

  newDestaque = false;

  loading = false;

  saving = false;

  confirmation = {
    show: false,
    message: '',
    onChoice: (() => {}) as (choice: boolean) => void,
  };

  private onKeywordInput = debounce(() => this.reload(), 300);

  get hasMore(): boolean {
    return this.items.length < this.total;
  }

  mounted(): void {
    this.reload();
  }

  @Watch('active')
  onActiveChange(active: boolean): void {
    if (active) this.reload();
  }

  @Watch('clientId')
  onClientChange(): void {
    this.reload();
  }

  async reload(): Promise<void> {
    this.items = [];
    this.total = 0;
    await this.loadPage(0);
  }

  async loadMore(): Promise<void> {
    await this.loadPage(this.items.length);
  }

  private async loadPage(offset: number): Promise<void> {
    if (!this.clientId) return;
    this.loading = true;
    try {
      const page = await this.clientNoteService.list(
        this.clientId,
        this.clientType,
        pageLimit,
        offset,
        (this.keyword || '').trim(),
      );
      this.items = offset === 0 ? page.results : [...this.items, ...page.results];
      this.total = page.total;
    } catch (e) {
      this.$notify.error(this.$t('crm.clientNotes.loadError').toString());
    } finally {
      this.loading = false;
    }
  }

  async onAdd(): Promise<void> {
    const observacao = this.newObservacao.trim();
    if (!observacao) return;
    this.saving = true;
    try {
      await this.clientNoteService.create(this.clientId, this.clientType, observacao, this.newDestaque);
      this.newObservacao = '';
      this.newDestaque = false;
      this.$notify.success(this.$t('crm.clientNotes.saved').toString());
      await this.reload();
      this.$emit('changed');
    } catch (e) {
      this.$notify.error(this.$t('crm.clientNotes.saveError').toString());
    } finally {
      this.saving = false;
    }
  }

  async onToggleHighlight(item: ClientNoteModel): Promise<void> {
    const destaque = !item.flagDestaque;
    if (!destaque) {
      this.setHighlight(item, false);
      return;
    }
    this.saving = true;
    let moving = false;
    try {
      moving = await this.hasOtherHighlight(item);
    } finally {
      this.saving = false;
    }
    if (!moving) {
      this.setHighlight(item, true);
      return;
    }
    // Só um destaque por cliente: confirmar antes de mover o destaque de outra anotação.
    this.confirmation.message = this.$t('crm.clientNotes.moveHighlightConfirm').toString();
    this.confirmation.onChoice = (choice: boolean) => {
      this.confirmation.show = false;
      if (choice) this.setHighlight(item, destaque);
    };
    this.confirmation.show = true;
  }

  // A página carregada pode estar filtrada pela busca; o destaque vigente vem do servidor.
  private async hasOtherHighlight(item: ClientNoteModel): Promise<boolean> {
    try {
      const featured = await this.clientNoteService.featured(this.clientId, this.clientType);
      return !!featured.note && !!featured.note.flagDestaque && featured.note.id !== item.id;
    } catch (e) {
      return this.items.some((i) => i.flagDestaque && i.id !== item.id);
    }
  }

  private async setHighlight(item: ClientNoteModel, destaque: boolean): Promise<void> {
    this.saving = true;
    try {
      await this.clientNoteService.setHighlight(item.id, destaque);
      await this.reload();
      this.$emit('changed');
    } catch (e) {
      this.$notify.error(this.$t('crm.clientNotes.saveError').toString());
    } finally {
      this.saving = false;
    }
  }
}
